// src/components/study/OrganizeModal.tsx
// ✅ Todos os imports no topo (fix eslint import/first)
// ✅ Zero emojis — apenas ícones Lucide React

import React, { useState, useEffect, useCallback } from 'react';
import {
    X, FolderPlus, Check, Plus, Loader2,
    Folder, FolderHeart, FolderSearch, FolderClock, FolderKanban,
    Rocket, Zap, Sparkles, Coffee, Ghost, Star, Flame, Brain,
    Target, Trophy, Lightbulb, Music, Leaf, Shield, Crown,
    Compass, Archive, Tag, Pizza, Banana
} from 'lucide-react';
import './OrganizeModal.css';
import ReactDOM from 'react-dom';

const API_BASE = '/api';

// ========== MAPA DE ÍCONES (igual ao SavedFoldersView) ==========
const ICON_MAP: Record<string, React.ElementType> = {
    Folder, FolderHeart, FolderSearch, FolderClock, FolderKanban,
    Rocket, Zap, Sparkles, Coffee, Ghost, Star, Flame, Brain,
    Target, Trophy, Lightbulb, Music, Leaf, Shield, Crown,
    Compass, Archive, Tag, Pizza, Banana,
};

const ICON_OPTIONS: { id: string; label: string }[] = [
    { id: 'Folder', label: 'Pasta' },
    { id: 'FolderHeart', label: 'Favoritos' },
    { id: 'FolderSearch', label: 'Pesquisa' },
    { id: 'FolderClock', label: 'Recentes' },
    { id: 'FolderCode2', label: 'Código' },
    { id: 'FolderKanban', label: 'Projeto' },
    { id: 'Rocket', label: 'Lançamento' },
    { id: 'Zap', label: 'Rápido' },
    { id: 'Sparkles', label: 'Destaque' },
    { id: 'Coffee', label: 'Casual' },
    { id: 'Ghost', label: 'Especial' },
    { id: 'Star', label: 'Estrela' },
    { id: 'Flame', label: 'Em fogo' },
    { id: 'Brain', label: 'Conhecimento' },
    { id: 'Target', label: 'Foco' },
    { id: 'Trophy', label: 'Conquista' },
    { id: 'Lightbulb', label: 'Ideias' },
    { id: 'Music', label: 'Música' },
    { id: 'Leaf', label: 'Natureza' },
    { id: 'Shield', label: 'Importante' },
    { id: 'Crown', label: 'Top' },
    { id: 'Compass', label: 'Explorar' },
    { id: 'Pizza', label: 'Descanso' },
    { id: 'Banana', label: 'Inusitado' },
];

const getIcon = (id: string): React.ElementType => ICON_MAP[id] ?? Folder;

// ========== INTERFACES ==========
interface SavedFolder {
    _id: string;
    name: string;
    icon: string;
    color: string;
    cardCount: number;
}

interface OrganizeModalProps {
    isOpen: boolean;
    savedCardId: string | null;
    onClose: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    fromRyan?: boolean;
}

// ========== COMPONENTE ==========
const OrganizeModal: React.FC<OrganizeModalProps> = ({ isOpen, savedCardId, onClose, onShowToast, fromRyan = false }) => {
    const FOLDERS_API = fromRyan ? '/api/ryan/saved/folders' : `${API_BASE}/saved-folders`;
    const [folders, setFolders] = useState<SavedFolder[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [showCreateInput, setShowCreateInput] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderIcon, setNewFolderIcon] = useState('Folder'); // ← id do ícone, não emoji
    const [isCreating, setIsCreating] = useState(false);

    const fetchData = useCallback(async () => {
        if (!savedCardId) return;
        setIsLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const headers = { Authorization: `Bearer ${token}` };

            const [foldersRes, cardFoldersRes] = await Promise.all([
                fetch(FOLDERS_API, { headers }),
                fetch(`${FOLDERS_API}/card-folders/${savedCardId}`, { headers })
            ]);

            if (foldersRes.ok) {
                const data = await foldersRes.json();
                setFolders(data.folders || []);
            }
            if (cardFoldersRes.ok) {
                const data = await cardFoldersRes.json();
                setSelectedIds(new Set(data.folderIds || []));
            }
        } catch (err) {
            console.error('[OrganizeModal] Erro ao carregar:', err);
        } finally {
            setIsLoading(false);
        }
    }, [savedCardId, FOLDERS_API]);

    useEffect(() => {
        if (isOpen && savedCardId) fetchData();
    }, [isOpen, savedCardId, fetchData]);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        if (isOpen) document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    useEffect(() => {
        const page = document.querySelector('.study-page-final');
        if (isOpen) {
            page?.classList.add('page-blurred');
        } else {
            page?.classList.remove('page-blurred');
        }
        return () => page?.classList.remove('page-blurred');
    }, [isOpen]);

    const toggleFolder = (folderId: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(folderId)) next.delete(folderId);
            else next.add(folderId);
            return next;
        });
    };

    const handleSave = async () => {
        if (!savedCardId) return;
        setIsSaving(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${FOLDERS_API}/sync-card`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ savedCardId, folderIds: Array.from(selectedIds) })
            });
            if (response.ok) {
                const data = await response.json();
                if (data.added + data.removed > 0) onShowToast('Pastas atualizadas', 'success');
                onClose();
            } else {
                onShowToast('Erro ao salvar', 'error');
            }
        } catch {
            onShowToast('Erro ao salvar', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        setIsCreating(true);
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(FOLDERS_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    name: newFolderName.trim(),
                    icon: newFolderIcon  // ← envia id do ícone ("Rocket", "Brain", etc.)
                })
            });
            if (response.ok) {
                const data = await response.json();
                setFolders(prev => [...prev, data.folder]);
                setSelectedIds(prev => new Set([...prev, data.folder._id]));
                setNewFolderName('');
                setNewFolderIcon('Folder');
                setShowCreateInput(false);
            } else {
                const err = await response.json();
                onShowToast(err.message || 'Erro ao criar pasta', 'error');
            }
        } catch {
            onShowToast('Erro ao criar pasta', 'error');
        } finally {
            setIsCreating(false);
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="organize-overlay" onClick={onClose}>
            <div className="organize-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="organize-header">
                    <div className="organize-header-left">
                        <FolderPlus size={18} className="organize-header-icon" />
                        <h3>Organizar em pastas</h3>
                    </div>
                    <button className="organize-close" onClick={onClose} aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="organize-body">
                    {isLoading ? (
                        <div className="organize-loading">
                            <Loader2 size={22} className="spin" />
                            <span>Carregando pastas...</span>
                        </div>
                    ) : (
                        <>
                            {/* Lista de pastas existentes */}
                            {folders.length === 0 && !showCreateInput ? (
                                <div className="organize-empty">
                                    <Folder size={32} strokeWidth={1.5} />
                                    <p>Nenhuma pasta ainda.</p>
                                    <p className="organize-empty-sub">Crie sua primeira pasta abaixo.</p>
                                </div>
                            ) : (
                                <div className="organize-folder-list">
                                    {folders.map(folder => {
                                        const isSelected = selectedIds.has(folder._id);
                                        const IconComp = getIcon(folder.icon);
                                        return (
                                            <button
                                                key={folder._id}
                                                className={`organize-folder-item ${isSelected ? 'selected' : ''}`}
                                                onClick={() => toggleFolder(folder._id)}
                                                style={isSelected ? { borderColor: folder.color } : undefined}
                                            >
                                                {/* ✅ Ícone Lucide — não texto */}
                                                <span
                                                    className="organize-folder-icon"
                                                    style={{ color: isSelected ? folder.color : undefined }}
                                                >
                                                    <IconComp size={16} strokeWidth={1.8} />
                                                </span>
                                                <span className="organize-folder-name">{folder.name}</span>
                                                <span className="organize-folder-count">{folder.cardCount}</span>
                                                <span className={`organize-folder-check ${isSelected ? 'visible' : ''}`}>
                                                    <Check size={14} strokeWidth={2.5} />
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Criar nova pasta inline */}
                            {showCreateInput ? (
                                <div className="organize-create-inline">
                                    {/* ✅ Icon picker com Lucide — zero emojis */}
                                    <div className="organize-icon-picker">
                                        {ICON_OPTIONS.map(({ id, label }) => {
                                            const Icon = getIcon(id);
                                            return (
                                                <button
                                                    key={id}
                                                    className={`icon-option ${newFolderIcon === id ? 'active' : ''}`}
                                                    onClick={() => setNewFolderIcon(id)}
                                                    title={label}
                                                    type="button"
                                                >
                                                    <Icon size={15} strokeWidth={1.8} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="organize-create-input-row">
                                        <input
                                            type="text"
                                            placeholder="Nome da pasta..."
                                            value={newFolderName}
                                            onChange={e => setNewFolderName(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') handleCreateFolder();
                                                if (e.key === 'Escape') setShowCreateInput(false);
                                            }}
                                            autoFocus
                                            maxLength={60}
                                        />
                                        <button
                                            className="btn-create-confirm"
                                            onClick={handleCreateFolder}
                                            disabled={isCreating || !newFolderName.trim()}
                                        >
                                            {isCreating ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                                        </button>
                                        <button
                                            className="btn-create-cancel"
                                            onClick={() => { setShowCreateInput(false); setNewFolderName(''); }}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button className="organize-btn-new-folder" onClick={() => setShowCreateInput(true)}>
                                    <Plus size={15} />
                                    Nova pasta
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="organize-footer">
                    <button className="organize-btn-cancel" onClick={onClose}>Cancelar</button>
                    <button className="organize-btn-save" onClick={handleSave} disabled={isSaving || isLoading}>
                        {isSaving
                            ? <><Loader2 size={14} className="spin" />Salvando...</>
                            : <><Check size={14} />Confirmar</>
                        }
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default OrganizeModal;